<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { tags } from '@lezer/highlight';
  import {
    Button,
    ButtonDropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Tooltip
  } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { GroupsService } from '@client/services/groups';
  import { RoutingService } from '@client/services/routing';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { userStore } from '@client/stores/user';

  import { GROUP_TYPES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  export let route = {};
  export let dropIndex = null;
  export let routeIndex = null;
  export let startIndex = null;
  export let reordering = false;
  export let priority = [];
  export let updatePriorityPositions = () => {};
  export let onDelete = () => {};
  export let onRefresh = () => {};
  export let senderGroups = [];
  export let selectedRoute = null;

  /**
   * Whether the page is in selection mode. Renders a checkbox, suppresses the
   * kebab menu, and turns card clicks into toggles.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * Whether this route is currently selected.
   * @type {boolean}
   */
  export let selected = false;

  /**
   * Called with this route when the card or its checkbox is clicked while selecting.
   * @type {(route: Object) => void}
   */
  export let onSelect = () => {};

  const { basepath } = getContext('config');

  let cache = {};
  let routeStatus = route.status;
  let isUpdating = false;

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const drop = (e) => {
    e.dataTransfer.dropEffect = 'move';
    priority = updatePriorityPositions(priority, startIndex, dropIndex);
    dropIndex = null;
    startIndex = null;
  };

  const dragstart = (e) => {
    if (!reordering) {
      e.preventDefault();
      return false;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';

    startIndex = routeIndex;
  };

  const resizeIframe = function () {
    this.style.visibility = 'visible';
    this.style.width = '100%';
    this.style.height = (this.contentWindow.document.body.firstChild.clientHeight + 8) / 4 + 'px';
  };

  const getUserSignature = async ({ blockname, groups = [] }) => {
    let email = $userStore.currentUser.email;
    let getSender = groups.length && groups[0].senders.length;

    if (cache[blockname]) {
      return cache[blockname];
    }

    if (getSender) {
      email = groups[0].senders[0].username || email;

      if (email === '*') {
        email = $userStore.currentUser.email;
      }
    }

    const data = {
      username: email,
      scratchblockname: blockname,
      testData: {
        testsender: email
      }
    };

    const { rendered } = await SignaturesService.getSignature(data);

    cache[blockname] = rendered || '-';

    return rendered;
  };

  const onDeleteHandler = async () => {
    const confirmed = confirm($format('label.DELETE_ROUTE_CONFIRMATION'));

    if (confirmed) {
      isUpdating = true;

      priority = updatePriorityPositions(priority, route.priority, -1);

      const response = await RoutingService.deleteRoute(route);

      isUpdating = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.DELETE_ROUTE'), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE_ROUTE'));

      onDelete(route);
    }
  };

  const onSave = async () => {
    isUpdating = true;

    const tags = (route.selectedTags || []).map(({ value }) => ({ text: value }));
    const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
    const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
    const { blockname, status, tagblock = {}, _id } = route;

    const payload = {
      _id,
      blockname,
      hide: route.hide,
      priority: route.priority - 1,
      sendergroups,
      status: routeStatus,
      tagblock,
      tags
    };

    const response = await RoutingService.updateRoute(payload);
    const sgroups = senderGroups.filter(
      ({ groupname }) => sendergroups.filter((group) => groupname === group.groupname).length
    );

    route = {
      ...response,
      ...route,
      selectedTags: route.selectedTags || [],
      selectedGroups: route.selectedGroups || [],
      sendergroups: sgroups,
      status: routeStatus,
      editing: false
    };

    priority = priority.map((item) => (!item._id || item?._id === route._id ? route : item));

    onRefresh();

    isUpdating = false;
  };

  const onEditRoute = () => {
    selectedRoute = deepCopy(route);
  };

  const onDisableRoute = () => {
    routeStatus = false;
    onSave();
  };

  const onEnableRoute = () => {
    routeStatus = true;
    onSave();
  };

  // Links and the kebab keep working while selecting, so clicks on them are not toggles.
  const onCardClick = ({ target }) => {
    if (!selecting || target.closest('a, button, input')) {
      return;
    }

    onSelect(route);
  };

  $: hasTags = route?.tagblock?.params?.length;
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="route"
  class:reordering
  class:disabled={!reordering}
  class:active={reordering && dropIndex === routeIndex}
  class:selecting
  class:border-primary={selected}
  class:bg-primary-subtle={selected}
  draggable={reordering}
  role="button"
  tabindex="0"
  class:groups={route.sendergroups?.length}
  class:tags={route.selectedTags?.length}
  on:dragstart={dragstart}
  on:drop|preventDefault={drop}
  on:dragover|preventDefault
  on:dragenter|preventDefault={() => (dropIndex = routeIndex)}
  on:click={onCardClick}
>
  <Loader backdrop show={isUpdating} />

  {#if reordering}
    <Icon name="grip-vertical" class="position-absolute text-body-tertiary text-large cursor-move" style="left: 8px" />
  {/if}

  {#if selecting}
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div class="select position-absolute" style="left: 8px" on:click|stopPropagation>
      <input
        class="form-check-input"
        type="checkbox"
        checked={selected}
        on:change={() => onSelect(route)}
        aria-label="{$format('label.SELECT')} {route.blockname}"
      />
    </div>
  {/if}

  <div class="priority-badge" class:active={route.status}>{route.priority}</div>

  <div class="route-section">
    {#key route.blockname}
      <div class="source">
        {#await getUserSignature({ blockname: route.blockname, groups: route.sendergroups })}
          <div class="loading">
            <Spinner inline show />
          </div>
        {:then html}
          <iframe
            tabindex="-1"
            style="width: 100%; visibility: hidden"
            scrolling="no"
            on:load={resizeIframe}
            title={route.blockname}
            srcdoc="<html><body><div style='transform: scale(.25,.25); transform-origin: left top; -moz-transform:  scale(.25,.25); -moz-transform-origin: left top;'>{html ||
              ''}</div></body></html>"
          />
        {:catch error}
          <p style="color: red">{error.message}</p>
        {/await}
      </div>
    {/key}

    {#if route.header}
      <div class="header-info">
        <Icon name="arrow-up" />
        {$format('label.INSERTS_AT_TOP')}
      </div>
    {/if}

    {#if route.hide}
      <div class="header-info">
        <Icon name="eye-off" />
        {$format('label.HIDDEN_FROM_ADDIN')}
      </div>
    {/if}
  </div>

  <div class="route-section">
    <b>{hasTags ? 'Signature' : 'Banner'}</b>
    <a href="{basepath}/signatures/studio/{route.blockname}" use:link class="item blockname">{route.blockname}</a>
  </div>

  {#if route.sendergroups?.length}
    <div class="route-section">
      <b>{$format('label.GROUPS')}</b>
      <div class="items">
        {#each route.sendergroups as group, index}
          {@const hasGroups = Object.keys(GROUP_TYPES).some((type) => group[type].length)}
          <div class="item">
            <!--
              The member list hangs off the name link, not the chip: the override glyph
              sits inside the chip too, so a chip-wide target would fire both tooltips
              at once and read as one merged callout.
            -->
            <a
              href="{basepath}/manage/groups/{group.groupname}"
              id="tooltip-{route.blockname}-{group.groupname}-{index}"
              use:link>{group.groupname}</a
            >
            {#if group?.outlookaddin?.enableOverrides}
              <span class="override" id="override-{route.blockname}-{group.groupname}-{index}">
                <Icon name="signpost" size={14} />
              </span>
              <Tooltip target="override-{route.blockname}-{group.groupname}-{index}" placement="top">
                {$format('label.ADDIN_OVERRIDES')}
              </Tooltip>
            {/if}
            {#if group.senders?.length}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {@html group.senders
                  .map(({ username }) => (username === '*' ? $format('label.ALL_USERS') : username))
                  .sort()
                  .join('<br>') || $format('label.EMPTY')}
              </Tooltip>
            {:else if hasGroups}
              {#each Object.keys(GROUP_TYPES) as type}
                {#if group[type]?.length}
                  {@const groupConfig = GROUP_TYPES[type]}
                  <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                    {@html group[type]
                      .map((item) => {
                        const name = Array.isArray(groupConfig.name)
                          ? groupConfig.name.map((value) => item[value]).join(' ')
                          : item[groupConfig.name];
                        return name + (item[groupConfig.count] ? ` (${item[groupConfig.count]})` : '');
                      })
                      .sort()
                      .join('<br>') || $format(groupConfig.genericLabel)}
                  </Tooltip>
                {/if}
              {/each}
            {:else}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {group.userAttrs?.length ? $format('label.DYNAMIC_GROUP') : $format('label.EMPTY')}
              </Tooltip>
            {/if}
          </div>
        {/each}
      </div>
    </div>
  {/if}
  {#if route.selectedTags?.length}
    <div class="route-section" role="button" tabindex="0">
      <b>{$format('label.TAGS')}</b>
      <div class="items">
        {#each route.selectedTags as tag}
          <div class="item">
            {tag.value}
          </div>
        {/each}
      </div>
    </div>
  {/if}
  <div class="actions">
    {#if !reordering && !selecting}
      <ButtonDropdown>
        <DropdownToggle color="none" tag="span">
          <Icon name="more-vertical" class="action-icon" />
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem on:click={onEditRoute}>{$format('label.EDIT')}</DropdownItem>
          {#if route.status}
            <DropdownItem on:click={onDisableRoute}>{$format('label.DISABLE')}</DropdownItem>
          {:else}
            <DropdownItem on:click={onEnableRoute}>{$format('label.ENABLE')}</DropdownItem>
          {/if}
          <DropdownItem divider />
          <DropdownItem on:click={onDeleteHandler}>{$format('label.DELETE')}</DropdownItem>
        </DropdownMenu>
      </ButtonDropdown>
    {/if}
  </div>
</div>

<style lang="scss" src="./route.scss"></style>
